import { useTranslation } from 'react-i18next';
import { useRequiredParams } from 'app/router/params';
import { useEffect, useState, FormEvent, ChangeEvent } from 'react';
import { useList, useUpdateList } from 'entities/list';

import { IconButton, Modal } from 'app/components';

interface ListValue {
  id?: number;
  code: string;
  value: string;
  sortOrder: string;
}

interface List {
  id?: number;
  code?: string;
  name: string;
  values: ListValue[];
}

interface ItemEditProps {
  item?: ListValue;
  closeModal: () => void;
  addItem: (item: { code: string; value: string; sortOrder: string }) => void;
  updateItem: (item: ListValue) => void;
  busy?: boolean;
}

const ItemEdit = ({ item = undefined, closeModal, addItem, updateItem, busy = false }: ItemEditProps) => {
  const { t } = useTranslation(['common', 'lists']);

  const [code, setCode] = useState(item?.code || '');
  const [value, setValue] = useState(item?.value || '');
  const [sortOrder, setSortOrder] = useState(item?.sortOrder || '0');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (code && value && sortOrder) {
      if (item) {
        updateItem({ ...item, code, value, sortOrder });
      } else {
        addItem({ code, value, sortOrder });
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <label className={'col-form-label'} htmlFor="code">
        {t('lists:values.code')}
      </label>
      <input className="form-control" type="text" name="name" id="name" value={code} onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)} />
      <label className={'col-form-label'} htmlFor="value">
        {t('lists:values.value')}
      </label>
      <input
        className="form-control"
        type="text"
        name="value"
        id="value"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
      />
      <label className={'col-form-label'} htmlFor="sortOrder">
        {t('lists:values.sortorder')}
      </label>
      <input
        className="form-control"
        type="text"
        name="sortOrder"
        id="value"
        value={sortOrder}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSortOrder(e.target.value)}
      />
      <div className="mt-4 d-flex justify-content-end">
        <button className="button clear" type="button" onClick={closeModal}>
          {t('common:cancel')}
        </button>
        <button className="button" disabled={busy}>
          {t('common:save')}
        </button>
      </div>
    </form>
  );
};

const ListEdit = () => {
  const { t } = useTranslation(['common', 'lists']);

  const [name, setName] = useState('');
  const [listValues, setListValues] = useState<ListValue[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [listItemToEdit, setListItemToEdit] = useState<ListValue | undefined>();

  const { id } = useRequiredParams('id');
  const { data: list } = useList(id);
  const { mutate: updateList } = useUpdateList(id);

  useEffect(() => {
    if (list) {
      const typedList = list as List;
      setName(typedList.name);
      setListValues(typedList.values);
      closeModal();
      setBusy(false);
    }
  }, [list]);

  function closeModal() {
    setShowModal(false);
  }

  function handleNewClick() {
    setShowModal(true);
  }

  function handleListClick(item: ListValue) {
    setListItemToEdit(item);
    setShowModal(true);
  }

  function handleAddItem(item: { code: string; value: string; sortOrder: string }) {
    const updatedItems = [...listValues, item as ListValue];
    doUpdate({ ...(list as List), values: updatedItems });
  }

  function handleUpdateItem(item: ListValue) {
    const updatedItems = listValues.map((v) => {
      if (item.id === v.id) {
        return item;
      }

      return v;
    });

    doUpdate({ ...(list as List), values: updatedItems });
  }

  function handleSubmit(e?: FormEvent) {
    if (e) {
      e.preventDefault();
    }

    setIsSubmitted(true);

    if (name) {
      const updatedList = { ...(list as List), name, values: listValues };
      doUpdate(updatedList);
    }
  }

  function doUpdate(list: List) {
    setBusy(true);

    updateList(list, {
      onSuccess: (data) => {
        if (data) {
          setName(data.name);
          setListValues(data.values);
          closeModal();
          setBusy(false);
          setListItemToEdit(undefined);
        }
      },
    });
  }

  return (
    <>
      <div className="content-title">
        <h1 className="">{t('lists:title')}</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className={'col-form-label'} htmlFor="name">
              {t('lists:name')}
            </label>
            <input
              className="form-control"
              type="text"
              name="name"
              id="name"
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
            {isSubmitted && !name ? <div className="invalid-feedback d-block">Please enter a name.</div> : null}
          </div>

          <table>
            <thead>
              <tr>
                <th>{t('lists:values.code')}</th>
                <th>{t('lists:values.value')}</th>
                <th>{t('lists:values.sortorder')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listValues.map((item, index) => {
                return (
                  <tr key={index} onClick={() => handleListClick(item)}>
                    <td>{item.code}</td>
                    <td>{item.value}</td>
                    <td>{item.sortOrder}</td>
                    <td>
                      <IconButton icon="pen" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-4 d-flex justify-content-between">
            <IconButton onClick={handleNewClick} icon="plus" />
            <button className="button light-blue" type="submit" disabled={busy}>
              {t('common:save')}
            </button>
          </div>
        </form>
        {showModal ? (
          <Modal titleLabelKey="organisations:new-org.modal.title" show={showModal} onClose={closeModal}>
            <ItemEdit item={listItemToEdit} closeModal={closeModal} addItem={handleAddItem} updateItem={handleUpdateItem} busy={busy} />
          </Modal>
        ) : null}
      </div>
    </>
  );
};

export default ListEdit;
